import { register, CloudObject, defineProperty } from 'olympe';

/**
 * @extends {CloudObject}
 */
export default class MSGraphTask extends CloudObject {

    /**
     * Creates an instance of MSGraphTask.
     *
     * @param {!Transaction} transaction
     * @return {!string} the tag of the created instance
     */
    static create(transaction) {
        return transaction.create(MSGraphTask);
    }
    
    /**
     * @return {string}
     */
    getPlanId() {
        return this.get(MSGraphTask.planIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePlanId($) {
        return this.observe($, MSGraphTask.planIdProp);
    }

    /**
     * @return {string}
     */
    getBucketId() {
        return this.get(MSGraphTask.bucketIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeBucketId($) {
        return this.observe($, MSGraphTask.bucketIdProp);
    }

    /**
     * @return {string}
     */
    getTitle() {
        return this.get(MSGraphTask.titleProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeTitle($) {
        return this.observe($, MSGraphTask.titleProp);
    }

    /**
     * @return {string}
     */
    getId() {
        return this.get(MSGraphTask.idProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeId($) {
        return this.observe($, MSGraphTask.idProp);
    }

    /**
     * @return {string}
     */
    getCreatedBy() {
        return this.get(MSGraphTask.createdByProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCreatedBy($) {
        return this.observe($, MSGraphTask.createdByProp);
    }

    /**
     * @return {string}
     */
    getCreatedDateTime() {
        return this.get(MSGraphTask.createdDateTimeProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCreatedDateTime($) {
        return this.observe($, MSGraphTask.createdDateTimeProp);
    }

    /**
     * @return {string}
     */
    getAssignedDateTime() {
        return this.get(MSGraphTask.assignedDateTimeProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeAssignedDateTime($) {
        return this.observe($, MSGraphTask.assignedDateTimeProp);
    }

    /**
     * @return {string}
     */
    getAssignedByUser() {
        return this.get(MSGraphTask.assignedByUserProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeAssignedByUser($) {
        return this.observe($, MSGraphTask.assignedByUserProp);
    }

    /**
     * @return {Date}
     */
    getDueDateTime() {
        return this.get(MSGraphTask.dueDateTimeProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<Date>}
     */
    observeDueDateTime($) {
        return this.observe($, MSGraphTask.dueDateTimeProp);
    }

    /**
     * @return {string}
     */
    getAssignedTo() {
        return this.get(MSGraphTask.assignedToProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeAssignedTo($) {
        return this.observe($, MSGraphTask.assignedToProp);
    }

    /**
     * @return {number}
     */
    getPercentComplete() {
        return this.get(MSGraphTask.percentCompleteProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<number>}
     */
    observePercentComplete($) {
        return this.observe($, MSGraphTask.percentCompleteProp);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPlanId(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.planIdProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setBucketId(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.bucketIdProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setTitle(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.titleProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setId(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.idProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCreatedBy(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.createdByProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCreatedDateTime(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.createdDateTimeProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setAssignedDateTime(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.assignedDateTimeProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setAssignedByUser(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.assignedByUserProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {Date} value
     */
    static setDueDateTime(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.dueDateTimeProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setAssignedTo(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.assignedToProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {number} value
     */
    static setPercentComplete(transaction, instance, value) {
        transaction.update(instance, MSGraphTask.percentCompleteProp, value);
    }

}

// Hardcoded tags
register('018ddf4dd51b38231fa1', MSGraphTask);
MSGraphTask.planIdProp = defineProperty('018ddf4e366b0a7f5b99');
MSGraphTask.bucketIdProp = defineProperty('018ddf4e45ea14c58657');
MSGraphTask.titleProp = defineProperty('018ddf4e4cb9b884563c');
MSGraphTask.idProp = defineProperty('018ddf4f29cc46d2f477');
MSGraphTask.createdByProp = defineProperty('018ddf504fd811dd63d1');
MSGraphTask.createdDateTimeProp = defineProperty('018ddf509f6ff66e0244');
MSGraphTask.assignedDateTimeProp = defineProperty('018ddf50db5631f393b9');
MSGraphTask.assignedByUserProp = defineProperty('018ddf573d80ef52c485');
MSGraphTask.dueDateTimeProp = defineProperty('018de5774c8a8b7e797f');
MSGraphTask.assignedToProp = defineProperty('018de5832831383f8bbc');
MSGraphTask.percentCompleteProp = defineProperty('018de5864a20ad8e6e55');
